import Layout from "@/components/layouts/Layout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Mail, Phone, MessageSquare, Clock } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const Contact = () => {
  const { t } = useLanguage();

  return (
    <Layout>
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-b from-white to-gray-50 dark:from-nexus-dark dark:to-gray-900">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-nexus-gray dark:text-nexus-light mb-6">
              {t("contactUs.title")}
            </h1>
            <p className="text-xl text-gray-600 dark:text-nexus-light mb-8">
              {t("contactUs.description")}
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form & Info Section */}
      <section className="py-16 bg-white">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Contact Form */}
            <div>
              <h2 className="text-2xl font-bold text-nexus-gray mb-6">
                {t("contactUs.form.title") ?? "Get in Touch"}
              </h2>
              <form className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-nexus-gray mb-1">
                      {t("contactUs.form.firstName")}
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-400 focus:border-nexus-teal focus:outline-none focus:ring-1 focus:ring-nexus-teal"
                      placeholder={t("contactUs.form.firstName")}
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-nexus-gray mb-1">
                      {t("contactUs.form.lastName")}
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-400 focus:border-nexus-teal focus:outline-none focus:ring-1 focus:ring-nexus-teal"
                      placeholder={t("contactUs.form.lastName")}
                      required
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-nexus-gray mb-1">
                    {t("contactUs.form.email")}
                  </label>
                  <input
                    type="email"
                    id="email"
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-400 focus:border-nexus-teal focus:outline-none focus:ring-1 focus:ring-nexus-teal"
                    placeholder={t("contactUs.form.email")}
                    required
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-nexus-gray mb-1">
                    {t("contactUs.form.phone")}
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-400 focus:border-nexus-teal focus:outline-none focus:ring-1 focus:ring-nexus-teal"
                    placeholder={t("contactUs.form.phone")}
                  />
                </div>

                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-nexus-gray mb-1">
                    {t("contactUs.form.subject")}
                  </label>
                  <input
                    type="text"
                    id="subject"
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-400 focus:border-nexus-teal focus:outline-none focus:ring-1 focus:ring-nexus-teal"
                    placeholder={t("contactUs.form.subject")}
                    required
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-nexus-gray mb-1">
                    {t("contactUs.form.message")}
                  </label>
                  <textarea
                    id="message"
                    rows={5}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-gray-900 placeholder-gray-400 focus:border-nexus-teal focus:outline-none focus:ring-1 focus:ring-nexus-teal"
                    placeholder={t("contactUs.form.message")}
                    required
                  ></textarea>
                </div>

                <div className="flex items-start">
                  <input
                    type="checkbox"
                    id="consent"
                    className="mt-1 rounded text-nexus-teal focus:ring-nexus-teal"
                    required
                  />
                  <label htmlFor="consent" className="ml-2 text-sm text-gray-600">
                    {t("contactUs.form.consent")}
                  </label>
                </div>

                <Button type="submit" className="w-full bg-nexus-brand hover:bg-nexus-brand/90">
                  {t("contactUs.form.sendMessage")}
                </Button>
              </form>
            </div>

            {/* Contact Information */}
            <div>
              <div className="bg-gray-50 dark:bg-nexus-dark-light p-8 rounded-lg mb-8">
                <h2 className="text-2xl font-bold text-nexus-gray dark:text-nexus-light mb-6">
                  {t("contactUs.contactInfo.title")}
                </h2>
                <div className="space-y-6">
                  <div className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-nexus-teal/10 flex items-center justify-center flex-shrink-0">
                      <Mail className="h-5 w-5 text-nexus-teal" />
                    </div>
                    <div>
                      <h3 className="font-medium text-nexus-gray dark:text-nexus-light">
                        {t("contactUs.contactInfo.email.title")}
                      </h3>
                      {t("contactUs.contactInfo.email.emails").map((email: string, i: number) => (
                        <p key={i} className="text-gray-600">{email}</p>
                      ))}
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-nexus-yellow/10 flex items-center justify-center flex-shrink-0">
                      <Phone className="h-5 w-5 text-nexus-yellow" />
                    </div>
                    <div>
                      <h3 className="font-medium text-nexus-gray dark:text-nexus-light">
                        {t("contactUs.contactInfo.phone.title")}
                      </h3>
                      {t("contactUs.contactInfo.phone.numbers").map((phone: string, i: number) => (
                        <p key={i} className="text-gray-600">{phone}</p>
                      ))}
                    </div>
                  </div>

                  {/* <div className="flex gap-4">
                    <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <Clock className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-nexus-gray ">
                        {t("contactUs.contactInfo.businessHours.title")}
                      </h3>
                      {t("contactUs.contactInfo.businessHours.hours").map((hour: string, i: number) => (
                        <p key={i} className="text-gray-600">{hour}</p>
                      ))}
                    </div>
                  </div> */}
                </div>
              </div>

              <Card className="overflow-hidden">
                <div className="p-6 bg-nexus-teal text-white">
                  <h3 className="text-xl font-semibold mb-2">
                    {t("contactUs.urgentAssistance.title")}
                  </h3>
                  <p className="text-white/80 mb-4">
                    {t("contactUs.urgentAssistance.description")}
                  </p>
                  <div className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5" />
                    <span className="font-medium">{t("contactUs.urgentAssistance.liveChat")}</span>
                  </div>
                </div>
                <div className="p-6">
                  <Button asChild variant="outline" className="w-full border-nexus-teal text-nexus-teal hover:bg-nexus-teal/5">
                    <a href="#">{t("contactUs.urgentAssistance.buttonText")}</a>
                  </Button>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>
      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="container">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-nexus-gray mb-8 text-center">
              {t("contactUs.faq.title")}
            </h2>

            <div className="space-y-6">
              {t("contactUs.faq.questions").map((faq: any, index: number) => (
                <div key={index} className="border-b border-gray-200 pb-6">
                  <h3 className="text-lg font-medium text-nexus-gray mb-2">{faq.question}</h3>
                  <p className="text-gray-600">{faq.answer}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 text-center">
              <Button asChild variant="outline" className="border-nexus-teal text-nexus-teal hover:bg-nexus-teal/5">
                <a href="/faq">{t("contactUs.faq.viewAll")}</a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Contact;
